import { Component, Input, OnChanges } from '@angular/core';
import { IAttendanceReports } from '../../interfaces/attendance';

interface CalendarDay {
  day: number;
  state: 'previous' | 'current' | 'next';
  isToday: boolean;
  leaveDay: boolean;
  inRange: boolean;
  isWeekend: boolean;
  status?: string;
}

@Component({
  selector: 'app-calender',
  imports: [],
  templateUrl: './calender.html',
  styleUrl: './calender.scss'
})
export class Calender implements OnChanges {

  protected months: string[] = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  protected viewYear: number = new Date().getFullYear();
  protected viewMonth: number = new Date().getMonth() + 1;

  protected today = {
    date: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  };

  @Input() attendanceDays: number[] = [];

  @Input() attendance: IAttendanceReports | null = null;

  @Input() termStartDate = '';

  @Input() termEndDate = '';

  ngOnChanges(): void {
    const start = this.parseDate(this.termStartDate);

    if (start) {
      this.viewYear = start.getFullYear();
      this.viewMonth = start.getMonth() + 1;
    }
  }

  protected get firstDayOfMonth(): number {
    return new Date(this.viewYear, this.viewMonth - 1, 1).getDay();
  }

  protected get lastDateOfMonth(): number {
    return new Date(this.viewYear, this.viewMonth, 0).getDate();
  }

  protected get lastDateOfPreviousMonth(): number {
    return new Date(this.viewYear, this.viewMonth - 1, 0).getDate();
  }

  protected get lastDayOfMonth(): number {
    return new Date(this.viewYear, this.viewMonth - 1, this.lastDateOfMonth).getDay();
  }

  protected get days(): CalendarDay[] {
    const cells: CalendarDay[] = [];
    const statusMap = this.buildStatusMap();

    const leading = this.firstDayOfMonth;
    const currentDays = this.lastDateOfMonth;
    const trailing = 6 - this.lastDayOfMonth;

    let cellIndex = 0;
    const pushCell = (
      state: 'previous' | 'current' | 'next',
      day: number,
      options: { isToday: boolean; leaveDay: boolean },
    ): void => {
      const column = cellIndex % 7;
      cellIndex++;

      const date = this.monthDate(state, day);
      const isWeekend = column === 0 || column === 6;

      cells.push({
        day,
        state,
        isToday: options.isToday,
        leaveDay: options.leaveDay,
        inRange: this.isInTermRange(date),
        isWeekend,
        status: statusMap.get(this.dateKeyOf(date)),
      });
    };

    for (let i = 0; i < leading; i++) {
      const day = this.lastDateOfPreviousMonth - leading + 1 + i;
      pushCell('previous', day, { isToday: false, leaveDay: false });
    }

    for (let day = 1; day <= currentDays; day++) {
      pushCell('current', day, {
        isToday: this.isCurrentDay(day),
        leaveDay: (this.attendanceDays ?? []).includes(day),
      });
    }

    for (let i = 1; i <= trailing; i++) {
      pushCell('next', i, { isToday: false, leaveDay: false });
    }

    return cells;
  }

  protected previousMonth(): void {
    const target = this.viewMonth === 1
      ? { year: this.viewYear - 1, month: 12 }
      : { year: this.viewYear, month: this.viewMonth - 1 };

    const bound = this.termStartIndex();

    if (bound !== null && this.monthIndex(target.year, target.month) < bound) {
      return;
    }

    this.viewMonth = target.month;
    this.viewYear = target.year;
  }

  protected nextMonth(): void {
    const target = this.viewMonth === 12
      ? { year: this.viewYear + 1, month: 1 }
      : { year: this.viewYear, month: this.viewMonth + 1 };

    const bound = this.termEndIndex();

    if (bound !== null && this.monthIndex(target.year, target.month) > bound) {
      return;
    }

    this.viewMonth = target.month;
    this.viewYear = target.year;
  }

  private termStartIndex(): number | null {
    const start = this.parseDate(this.termStartDate);
    return start ? this.monthIndex(start.getFullYear(), start.getMonth() + 1) : null;
  }

  private termEndIndex(): number | null {
    const end = this.parseDate(this.termEndDate);
    return end ? this.monthIndex(end.getFullYear(), end.getMonth() + 1) : null;
  }

  private monthIndex(year: number, month: number): number {
    return year * 12 + month;
  }

  private isCurrentDay(day: number): boolean {
    return (
      day === this.today.date &&
      this.viewMonth === this.today.month &&
      this.viewYear === this.today.year
    );
  }

  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.slice(0, 10).split('-').map(Number);

    if (parts.length !== 3 || parts.some(Number.isNaN)) {
      return null;
    }

    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  private monthDate(
    state: 'previous' | 'current' | 'next',
    day: number,
  ): { year: number; month: number; day: number } {
    const year = this.viewYear;
    const month = this.viewMonth;

    if (state === 'previous') {
      return month === 1 ? { year: year - 1, month: 12, day } : { year, month: month - 1, day };
    }

    if (state === 'next') {
      return month === 12 ? { year: year + 1, month: 1, day } : { year, month: month + 1, day };
    }

    return { year, month, day };
  }

  private dateKeyOf(d: { year: number; month: number; day: number }): string {
    const month = String(d.month).padStart(2, '0');
    const date = String(d.day).padStart(2, '0');
    return `${d.year}-${month}-${date}`;
  }

  private isInTermRange(d: { year: number; month: number; day: number }): boolean {
    const key = this.dateKeyOf(d);
    const start = (this.termStartDate ?? '').slice(0, 10);
    const end = (this.termEndDate ?? '').slice(0, 10);

    if (!start && !end) {
      return true;
    }

    if (start && key < start) {
      return false;
    }

    if (end && key > end) {
      return false;
    }

    return true;
  }

  private buildStatusMap(): Map<string, string> {
    const map = new Map<string, string>();

    for (const record of this.attendance?.payload ?? []) {
      const key = (record.attendance_date ?? '').slice(0, 10);

      if (key) {
        map.set(key, record.attendance_status);
      }
    }

    return map;
  }

}
